import { createReducer } from '@reduxjs/toolkit';
import type { ICustomerBase, INotification, IRelationshipManager, IUserMailbox, IVault } from '../../api/domain';
import { Tenant } from '../../enum/tenant';
import {
    addNotificationSuccess,
    applicationInitialized,
    changeTenant,
    clearNotifications,
    disableScrolling,
    enableScrolling,
    hideProgressIndicator,
    hideStickyNavigationOnMobile,
    login,
    logout,
    selectCardType,
    selectCustomerBase,
    selectCustomerBaseForEServices,
    selectCustomerBaseForProducts,
    selectMailboxes,
    selectVault,
    setRelationshipManager,
    setUserIsProspect,
    showProgressIndicator,
    showStickyNavigationOnMobile,
    strongLogin,
} from './application-actions';
import { ProductCardType } from '../../enum/product-card-type';

interface IApplicationState {
    initialized: boolean;
    tenant: Tenant;
    isLoggedIn: boolean;
    displayProgressIndicator: boolean;
    strong: boolean;
    notifications: INotification[];
    uniqueNotification: boolean;
    scrollingEnabled: boolean;
    hideStickyNavigationOnMobile: boolean;
    selectedCardType: ProductCardType;
    selectedCustomerBase?: ICustomerBase;
    selectedCustomerBaseForProducts?: ICustomerBase;
    selectedCustomerBaseForEServices?: ICustomerBase;
    selectedMailboxes: IUserMailbox[];
    selectedVault?: IVault;
    relationshipManager?: IRelationshipManager;
    userIsProspect: boolean;
}

export type { IApplicationState };

export const initialState: IApplicationState = {
    initialized: false,
    tenant: Tenant.NO_TENANT,
    isLoggedIn: false,
    displayProgressIndicator: false,
    strong: false,
    notifications: [],
    uniqueNotification: false,
    selectedCardType: ProductCardType.DEBIT, // OBB-690 DEBIT should be the default
    scrollingEnabled: true,
    hideStickyNavigationOnMobile: false,
    selectedMailboxes: [],
    userIsProspect: false,
};

export const applicationReducer = createReducer(initialState, (builder) => {
    builder
        .addCase(applicationInitialized, (state) => {
            state.initialized = true;
        })
        .addCase(setRelationshipManager, (state, { payload: relationshipManager }) => {
            state.relationshipManager = relationshipManager;
        })
        .addCase(showProgressIndicator, (state) => {
            state.displayProgressIndicator = true;
        })
        .addCase(hideProgressIndicator, (state) => {
            state.displayProgressIndicator = false;
        })
        .addCase(addNotificationSuccess, (state, { payload: notification }) => {
            // OBB-497: Auto close after 5s
            notification.autoCloseAfter = 5_000;
            if (notification.unique) {
                state.notifications = [notification];
            } else if (!state.uniqueNotification) {
                state.notifications.push(notification);
            }

            state.uniqueNotification = notification.unique || state.uniqueNotification;
        })
        .addCase(clearNotifications, (state) => {
            state.notifications = [];
        })
        .addCase(setUserIsProspect, (state) => {
            state.userIsProspect = true;
        })
        .addCase(changeTenant, (state, { payload: tenant }) => {
            state.tenant = tenant;
        })
        .addCase(login, (state) => {
            state.isLoggedIn = true;
        })
        .addCase(strongLogin, (state) => {
            state.isLoggedIn = true;
            state.strong = true;
        })
        .addCase(logout, (state) => {
            state.isLoggedIn = false;
            state.strong = false;
        })
        .addCase(enableScrolling, (state) => {
            state.scrollingEnabled = true;
        })
        .addCase(disableScrolling, (state) => {
            state.scrollingEnabled = false;
        })
        .addCase(showStickyNavigationOnMobile, (state) => {
            state.hideStickyNavigationOnMobile = false;
        })
        .addCase(hideStickyNavigationOnMobile, (state) => {
            state.hideStickyNavigationOnMobile = true;
        })
        .addCase(selectCardType, (state, { payload: cardType }) => {
            state.selectedCardType = cardType;
        })
        .addCase(selectCustomerBase, (state, { payload: customerBase }) => {
            state.selectedCustomerBase = customerBase;
        })
        .addCase(selectCustomerBaseForProducts, (state, { payload: customerBase }) => {
            state.selectedCustomerBaseForProducts = customerBase;
        })
        .addCase(selectCustomerBaseForEServices, (state, { payload: customerBase }) => {
            state.selectedCustomerBaseForEServices = customerBase;
        })
        .addCase(selectMailboxes, (state, { payload: mailboxes }) => {
            state.selectedMailboxes = mailboxes;
        })
        .addCase(selectVault, (state, { payload: vault }) => {
            state.selectedVault = vault;
        });
});
